import { Adresse } from './adresse.model';
import { BienImmobilier } from './bien-immobilier.model';

/** Aligné sur {@code fr.gavicyconseil.dto.LogementDto}. */
export class Logement {
  idLogementDto!: string;
  bienDto?: BienImmobilier;
  typeUniteDto?: string;
  numeroDto?: string;
  surfaceDto?: number;
  nombrePiecesDto?: number;
  chauffageDto?: string;
  eauDto?: string;
  autrePartiesDto?: string;
  meubleDto?: boolean;
  statutFiscalDto?: string;
  colocationDto?: boolean;
  assuranceBailleurDto?: boolean;
  montantAnnuelAssuranceDto?: number;
  montantMensuelAssuranceLocataireDto?: number;
  clauseSolidariteDto?: string;
  clauseResolutoireDto?: string;
  /** Présente = fin de gestion locative (unité fermée). */
  dateFinGestionDto?: Date | string;
}

export const TYPES_UNITE = [
  'Studio',
  'T1',
  'T2',
  'T3',
  'T4',
  'T5',
  'Chambre',
  'Maison',
  'Parking',
] as const;

export type TypeUnite = (typeof TYPES_UNITE)[number];

/** Valeurs métier réutilisées hors listes déroulantes. */
export const TYPE_UNITE = {
  MAISON: 'Maison',
  PARKING: 'Parking',
} as const satisfies Record<string, TypeUnite>;

export const TYPES_CHAUFFAGE = [
  'Individuel gaz',
  'Individuel électrique',
  'Collectif',
  'Pompe à chaleur',
  'Autre',
] as const;

/** Valeurs chauffage spécifiques au parking (stockées dans {@code chauffageDto}). */
export const PARKING_CHAUFFAGE = {
  CHAUFFE: 'Chauffé',
  NON_CHAUFFE: 'Non chauffé',
} as const;

export const TYPES_PARKING_CHAUFFAGE = [
  PARKING_CHAUFFAGE.CHAUFFE,
  PARKING_CHAUFFAGE.NON_CHAUFFE,
] as const;

export type ParkingChauffage = (typeof TYPES_PARKING_CHAUFFAGE)[number];

export function isParkingChauffageValue(
  chauffage?: string | null
): chauffage is ParkingChauffage {
  const value = (chauffage ?? '').trim();
  return (
    value === PARKING_CHAUFFAGE.CHAUFFE ||
    value === PARKING_CHAUFFAGE.NON_CHAUFFE
  );
}

/** True si le parking est déclaré chauffé. */
export function isParkingChauffe(chauffage?: string | null): boolean {
  return (chauffage ?? '').trim() === PARKING_CHAUFFAGE.CHAUFFE;
}

export const TYPES_EAU = [
  'Individualisée',
  'Collective',
  'Incluse dans les charges',
] as const;

export const STATUTS_FISCAUX_NON_MEUBLE = [
  'Micro Foncier',
  'Régime réel',
] as const;

export const STATUTS_FISCAUX_MEUBLE = [
  'Micro BIC',
  'Régime réel',
] as const;

export function statutsFiscauxPourMeuble(): readonly string[] {
  return STATUTS_FISCAUX_MEUBLE;
}

export function statutsFiscauxPourNonMeuble(): readonly string[] {
  return STATUTS_FISCAUX_NON_MEUBLE;
}

export function isParking(typeUnite?: string | null): boolean {
  return (typeUnite ?? '').trim() === TYPE_UNITE.PARKING;
}

/**
 * Normalise une date en {@code yyyy-MM-dd} en calendrier local.
 * Évite le décalage d’un jour causé par {@code toISOString()} (UTC) sur
 * les dates « jour seul » renvoyées par l’API (ex. minuit CEST → veille UTC).
 */
export function toDateIso(value?: Date | string | null): string {
  if (!value) {
    return '';
  }
  if (typeof value === 'string') {
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return value;
    }
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      return formatLocalDateParts(parsed);
    }
    return value.length >= 10 ? value.slice(0, 10) : '';
  }
  return formatLocalDateParts(value);
}

function formatLocalDateParts(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function isUniteFermee(logement: Logement): boolean {
  return !!toDateIso(logement.dateFinGestionDto);
}

export function formatDateFr(value?: Date | string | null): string {
  const iso = toDateIso(value);
  if (!iso) {
    return '';
  }
  const [year, month, day] = iso.split('-');
  return `${day}/${month}/${year}`;
}

export function emptyBien(): BienImmobilier {
  const bien = new BienImmobilier();
  bien.adresseDto = new Adresse();
  return bien;
}

export function uniteLabel(logement: Logement): string {
  if (logement.bienDto?.loueEnEntierDto) {
    return 'Entier';
  }
  const parts = [logement.typeUniteDto, logement.numeroDto].filter(
    (part) => typeof part === 'string' && part.length > 0
  );
  return parts.join(' · ') || 'Logement';
}

/**
 * Libellé d'unité pour l'UI (i18n).
 * Un bien « loué en entier » stocke souvent {@code Maison} en base : on affiche
 * le libellé court « Entier », pas le type technique.
 */
export function formatUniteDisplay(
  logement: Logement,
  translate: (key: string) => string
): string {
  if (logement.bienDto?.loueEnEntierDto) {
    return translate('bien.unites.entier');
  }
  const type = logement.typeUniteDto?.trim();
  const typeKey = type ? `ref.typeUnite.${type}` : '';
  const typeTranslated = typeKey ? translate(typeKey) : '';
  const typeLabel =
    type && typeTranslated !== typeKey ? typeTranslated : type;
  const parts = [typeLabel, logement.numeroDto].filter(
    (part) => typeof part === 'string' && part.length > 0
  );
  return parts.join(' · ') || translate('bien.logementFallback');
}

/** Libellé « bien — unité » pour listes et sélecteurs. */
export function formatLogementChoiceLabel(
  logement: Logement,
  translate: (key: string) => string
): string {
  const bienTitre = logement.bienDto?.titreDto?.trim();
  const unite = formatUniteDisplay(logement, translate);
  return bienTitre ? `${bienTitre} — ${unite}` : unite;
}

export function uniteDetails(logement: Logement): string {
  const parts: string[] = [];
  const parking = isParking(logement.typeUniteDto);
  if (logement.surfaceDto) {
    parts.push(`${logement.surfaceDto} m²`);
  }
  if (!parking && logement.nombrePiecesDto) {
    const n = logement.nombrePiecesDto;
    parts.push(n === 1 ? '1 pièce' : `${n} pièces`);
  }
  if (logement.chauffageDto) {
    parts.push(logement.chauffageDto);
  }
  if (!parking) {
    parts.push(logement.meubleDto ? 'Meublé' : 'Non meublé');
  }
  if (!parking && logement.colocationDto) {
    parts.push('Colocation');
  }
  if (isUniteFermee(logement)) {
    parts.push(`Fermée le ${formatDateFr(logement.dateFinGestionDto)}`);
  }
  return parts.join(' · ');
}

export function compareUnites(a: Logement, b: Logement): number {
  const fermeeA = isUniteFermee(a);
  const fermeeB = isUniteFermee(b);
  if (fermeeA !== fermeeB) {
    return fermeeA ? 1 : -1;
  }

  const numeroA = (a.numeroDto ?? '').trim();
  const numeroB = (b.numeroDto ?? '').trim();

  if (numeroA && numeroB) {
    const byNumero = numeroA.localeCompare(numeroB, 'fr', {
      numeric: true,
      sensitivity: 'base',
    });
    if (byNumero !== 0) {
      return byNumero;
    }
  } else if (numeroA) {
    return -1;
  } else if (numeroB) {
    return 1;
  }

  const typeA = a.typeUniteDto ?? '';
  const typeB = b.typeUniteDto ?? '';
  return typeA.localeCompare(typeB, 'fr', { sensitivity: 'base' });
}
